import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCreateEnrollment, useEnrollments, useEventSubCatalog, useHelixCatalog, useRevokeEnrollment } from '../../api/hooks';
import { api } from '../../api/http';
import { ENROLLMENT_LABEL, FEATURE_LABELS } from '../../domain/labels';
import { buildCategoryTree, buildDottedTree, type TreeNode } from '../../domain/tree';
import type { EnrollmentScopePreview, FeatureBinding } from '../../domain/types';
import { Button, DataTable, Field, Loading, OneTimeSecret, PageHeader, Select, StatePill, TreeSelect } from '../../ui';
import { helixTitle } from '../helix/labels';

const CHAT_PRESET = { types: ['es:channel.chat.message@1'], helix: ['hx:chat.sendMessage'] };

export function BotEnrollmentsPage(): JSX.Element {
  const { botId = '' } = useParams();
  const enrollments = useEnrollments(botId);
  const create = useCreateEnrollment(botId);
  const revoke = useRevokeEnrollment(botId);
  const eventsub = useEventSubCatalog();
  const helix = useHelixCatalog();
  const [features, setFeatures] = useState<FeatureBinding[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [ttlMs, setTtlMs] = useState(7 * 24 * 60 * 60 * 1000);
  const [preview, setPreview] = useState<EnrollmentScopePreview[]>([]);
  const [hoverScope, setHoverScope] = useState<string | null>(null);

  useEffect(() => {
    void api.featuresCatalog().then(setFeatures).catch(() => setFeatures([]));
  }, []);

  const types = eventsub.data ?? [];
  const endpoints = helix.data ?? [];
  const nodes = useMemo(() => {
    const es = buildDottedTree(
      types
        .filter((t) => t.lifecycle?.status !== 'decommissioned')
        .map((t) => ({
          type: t.type,
          version: t.version,
          label: t.type,
        })),
    );
    prefixTree(es, 'es:');
    attachHints(es, types);
    const hx = buildCategoryTree(
      endpoints.map((e) => ({
        id: e.endpoint,
        category: e.category || 'autre',
        label: helixTitle(e.endpoint),
        extra: e.permission,
      })),
    );
    prefixTree(hx, 'hx:');
    const ft: TreeNode[] = features.map((f) => ({
      id: `ft:${f.id}`,
      label: FEATURE_LABELS[f.id] ?? f.label,
    }));
    return [
      { id: 'root-es', label: 'Abonnements EventSub', children: es },
      { id: 'root-hx', label: 'Opérations Helix', children: hx },
      { id: 'root-ft', label: 'Fonctionnalités', children: ft },
    ];
  }, [types, endpoints, features]);

  const selection = useMemo(() => splitSelection(selected), [selected]);

  useEffect(() => {
    if (!botId) return;
    const handle = setTimeout(() => {
      void api.previewEnrollmentScopes(botId, {
        types: selection.types,
        helixEndpoints: selection.helix,
        features: selection.features,
      }).then((res) => setPreview(res.scopes ?? []));
    }, 200);
    return () => clearTimeout(handle);
  }, [botId, selection.types.join(','), selection.helix.join(','), selection.features.join(',')]);

  const highlighted = useMemo(() => {
    if (!hoverScope) return new Set<string>();
    const row = preview.find((p) => p.scope === hoverScope);
    if (!row) return new Set<string>();
    const ids = new Set<string>();
    for (const src of row.sources) {
      if (src.kind === 'eventsub') ids.add(`es:${src.id}`);
      if (src.kind === 'helix') ids.add(`hx:${src.id}`);
      if (src.kind === 'feature') ids.add(`ft:${src.id}`);
    }
    return ids;
  }, [hoverScope, preview]);

  if (enrollments.isPending || eventsub.isPending || helix.isPending) return <Loading />;

  return (
    <>
      <PageHeader
        title="Invitations"
        actions={
          <>
            <Button
              onClick={() => setSelected(new Set([...CHAT_PRESET.types, ...CHAT_PRESET.helix]))}
            >
              Préréglage bot de chat
            </Button>
            <Button
              variant="primary"
              loading={create.isPending}
              onClick={() =>
                create.mutate({
                  types: selection.types,
                  helixEndpoints: selection.helix,
                  features: selection.features,
                  ttlMs,
                })
              }
            >
              Créer l’invitation
            </Button>
          </>
        }
      />
      {create.data?.url ? <OneTimeSecret value={create.data.url} /> : null}
      <div className="tbm-split tbm-split--wide">
        <TreeSelect nodes={nodes} selected={selected} onChange={setSelected} highlight={highlighted} />
        <aside className="tbm-card">
          <h2>Scopes demandés</h2>
          <Field label="Durée de validité">
            <Select value={String(ttlMs)} onChange={(e) => setTtlMs(Number(e.target.value))}>
              <option value={3600000}>1 heure</option>
              <option value={86400000}>24 heures</option>
              <option value={604800000}>7 jours</option>
            </Select>
          </Field>
          <ul className="tbm-scope-panel">
            {preview.map((row) => (
              <li
                key={row.scope}
                className={hoverScope === row.scope ? 'is-active' : ''}
                onMouseEnter={() => setHoverScope(row.scope)}
                onMouseLeave={() => setHoverScope(null)}
              >
                <code>{row.scope}</code>
                <p className="muted">{row.description}</p>
                <p className="muted">{row.sources.map((s) => `${s.kind}: ${s.id}`).join(' · ')}</p>
              </li>
            ))}
          </ul>
        </aside>
      </div>
      <DataTable
        rows={enrollments.data ?? []}
        empty="Aucune invitation."
        columns={[
          { key: 'id', header: 'Id', cell: (l) => <code className="tbm-id">{l.id}</code> },
          { key: 'st', header: 'Statut', cell: (l) => <StatePill state={l.status}>{ENROLLMENT_LABEL[l.status]}</StatePill> },
          {
            key: 'sel',
            header: 'Sélections',
            cell: (l) =>
              `${(l.desiredSubscriptions ?? []).length} EventSub · ${(l.desiredHelixEndpoints ?? []).length} Helix · ${(l.desiredFeatures ?? []).length} fonctions`,
          },
          {
            key: 'sc',
            header: 'Scopes',
            cell: (l) => (
              <details>
                <summary>{l.requestedScopes.length} scopes</summary>
                <p className="muted">{l.requestedScopes.join(', ')}</p>
              </details>
            ),
          },
          {
            key: 'a',
            header: '',
            cell: (l) =>
              l.status === 'active' ? (
                <Button size="sm" variant="danger" onClick={() => revoke.mutate(l.id)}>Révoquer</Button>
              ) : l.consumedByBroadcasterId ? (
                <Link to={`/bots/${botId}/channels/${l.consumedByBroadcasterId}`}>Chaîne</Link>
              ) : null,
          },
        ]}
      />
    </>
  );
}

function prefixTree(nodes: TreeNode[], prefix: string): void {
  for (const node of nodes) {
    if (!node.children?.length) node.id = node.id.startsWith(prefix) ? node.id : prefix + node.id;
    else prefixTree(node.children, prefix);
  }
}

function attachHints(nodes: TreeNode[], types: Array<{ type: string; version?: string; event?: Array<{ name: string }>; lifecycle?: { status?: string } }>): void {
  for (const node of nodes) {
    const raw = node.id.replace(/^es:/, '');
    const [type, version] = raw.split('@');
    const def = types.find((t) => t.type === type && (!version || t.version === version));
    if (def?.event?.length) node.hint = def.event.map((f) => f.name).join(', ');
    if (def?.lifecycle?.status === 'deprecated' || def?.lifecycle?.status === 'decommissioned' || def?.lifecycle?.status === 'beta') {
      node.disabled = def.lifecycle.status === 'decommissioned';
    }
    if (node.children) attachHints(node.children, types);
  }
}

export function splitSelection(selected: ReadonlySet<string>): { types: string[]; helix: string[]; features: string[] } {
  const types: string[] = [];
  const helix: string[] = [];
  const features: string[] = [];
  for (const id of selected) {
    if (id.startsWith('es:')) types.push(id.slice(3));
    else if (id.startsWith('hx:')) helix.push(id.slice(3));
    else if (id.startsWith('ft:')) features.push(id.slice(3));
  }
  return { types, helix, features };
}

export { BotEnrollmentsPage as EnrollmentsPage };
